import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUploadCloud, FiImage, FiCheck, FiZap, FiInfo, FiTrash2 } = FiIcons;

const UploadPhotoModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [isEnhanceEnabled, setIsEnhanceEnabled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => URL.createObjectURL(file));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePublish = () => {
    setIsUploading(true);
    // Simulate upload
    setTimeout(() => {
      onUpload(selectedImages);
      setIsUploading(false);
      onClose();
      setSelectedImages([]);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark"
      >
        <div className="p-8 md:p-12">
          <div className="flex justify-between items-start mb-10">
            <div>
              <h2 className="text-3xl font-serif text-accent-navy mb-2">Preserve a Memory</h2>
              <p className="text-sm text-text-muted font-light">Upload photos to the collective memorial gallery.</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6 text-text-muted" />
            </button>
          </div>

          <div className="space-y-8">
            {/* Upload Area */}
            {selectedImages.length === 0 ? (
              <label className="block cursor-pointer">
                <div className="border-4 border-dashed border-brand-dark rounded-[32px] p-16 text-center hover:border-accent-gold transition-colors bg-brand-light/20">
                  <div className="w-20 h-20 bg-white rounded-3xl shadow-sm flex items-center justify-center text-accent-gold mx-auto mb-6 border border-brand-dark/50">
                    <SafeIcon icon={FiUploadCloud} className="w-10 h-10" />
                  </div>
                  <p className="text-lg font-bold text-accent-navy mb-2">Drag photos here or click to browse</p>
                  <p className="text-xs text-text-muted uppercase tracking-widest font-medium">Supports JPG, PNG up to 20MB</p>
                  <input type="file" multiple className="hidden" onChange={handleFileChange} accept="image/*" />
                </div>
              </label>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  {selectedImages.map((img, idx) => (
                    <div key={idx} className="aspect-square rounded-2xl overflow-hidden relative group border-2 border-brand-dark">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeImage(idx)}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <label className="aspect-square rounded-2xl border-2 border-dashed border-brand-dark flex flex-col items-center justify-center text-text-muted hover:border-accent-gold hover:text-accent-gold transition-all cursor-pointer bg-brand-light/10">
                    <SafeIcon icon={FiIcons.FiPlus} className="w-8 h-8 mb-1" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Add More</span>
                    <input type="file" multiple className="hidden" onChange={handleFileChange} accept="image/*" />
                  </label>
                </div>

                {/* AI Enhancement Toggle */}
                <div className={`p-6 rounded-3xl border-2 transition-all flex items-center justify-between ${isEnhanceEnabled ? 'bg-accent-purple/5 border-accent-purple' : 'bg-brand-light border-brand-dark'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${isEnhanceEnabled ? 'bg-accent-purple text-white' : 'bg-white text-text-muted'}`}>
                      <SafeIcon icon={FiZap} className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className={`text-sm font-bold ${isEnhanceEnabled ? 'text-accent-purple' : 'text-accent-navy'}`}>AI Photo Restoration</h4>
                      <p className="text-[10px] text-text-muted font-medium uppercase tracking-widest">Gently fix grain and lighting</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsEnhanceEnabled(!isEnhanceEnabled)}
                    className={`w-14 h-8 rounded-full relative transition-colors ${isEnhanceEnabled ? 'bg-accent-purple' : 'bg-brand-dark'}`}
                  >
                    <motion.div 
                      animate={{ x: isEnhanceEnabled ? 28 : 4 }}
                      className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-md"
                    />
                  </button>
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-brand-light flex items-center justify-between gap-6">
              <div className="flex items-center gap-3 text-text-muted">
                <SafeIcon icon={FiInfo} className="w-4 h-4 text-accent-gold" />
                <p className="text-[10px] font-medium uppercase tracking-widest leading-relaxed">
                  Photos will be shared with the family <br /> and added to the public timeline.
                </p>
              </div>
              <button 
                onClick={handlePublish}
                disabled={selectedImages.length === 0 || isUploading}
                className="btn-primary py-4 px-10 text-lg flex items-center gap-3 min-w-[200px] justify-center"
              >
                {isUploading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Publish {selectedImages.length > 0 && `(${selectedImages.length})`}</>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UploadPhotoModal;